// ─────────────────────────────────────────────
//  voicePlan — turns a parsed voice intent + live board state into concrete,
//  human-confirmable proposals. Pure: returns declarative actions; the
//  VoiceControl component maps them onto the existing Firestore helpers, so
//  every write stays inside the same rules-checked paths the buttons use.
//
//  Proposal shapes:
//    { kind: "assign", roomId, roomName, providerId, providerName, label }
//    { kind: "break",  roomId, roomName, channel, giverId, giverName, label }
//    { kind: "answer", text }                       // display only, no confirm
// ─────────────────────────────────────────────
import { getChannelState } from "./breaks";

const DEFAULT_SUPERVISOR_LIMIT = 4;

const byRoomName = (assignments, roomName) =>
  assignments.find(
    (a) => a.orRoom === roomName || a.orRoom === String(roomName).replace(/^OR\s+/i, "")
  ) || null;

// Free CRNAs: today's bench (rostered, unassigned, not departed), in order.
function pickBench(bench, taken) {
  return (bench || []).find((p) => p && !taken.has(p.id)) || null;
}

export function planVoiceIntent(intent, state = {}) {
  const {
    rooms = [], assignments = [], bench = [], breakGivers = [],
    supervisorLimit = DEFAULT_SUPERVISOR_LIMIT,
  } = state;

  switch (intent?.type) {
    case "assign_crna":
    case "help_rooms": {
      const taken = new Set();
      const out = [];
      for (const rid of intent.roomIds || []) {
        const room = rooms.find((r) => r.id === rid);
        if (!room) continue;
        const row = byRoomName(assignments, room.name);
        const pick = pickBench(bench, taken);
        if (!pick) {
          out.push({
            kind: "answer",
            text: `No free CRNA on the bench for ${room.name} — everyone rostered is already in a room. Consider a transfer or calling in.`,
          });
          continue;
        }
        taken.add(pick.id);
        const verb = intent.type === "help_rooms" ? "Send" : "Assign";
        out.push({
          kind: "assign",
          roomId: room.id,
          roomName: room.name,
          assignmentId: row?.id || null,
          providerId: pick.id,
          providerName: pick.name,
          replaces: intent.type === "assign_crna" ? (row?.ahpName || "") : "",
          label: `${verb} ${pick.name} to ${room.name}${row?.ahpName && intent.type === "assign_crna" ? ` (replacing ${row.ahpName})` : row?.ahpName ? ` (helping ${row.ahpName})` : ""}`,
        });
      }
      if (!out.length) out.push({ kind: "answer", text: "I couldn't match those rooms on today's board." });
      return out;
    }

    case "rotate_breaks": {
      // Rooms with a CRNA whose lunch isn't done and no break running, paired
      // with rostered givers not already committed. Max 3 per rotation pass.
      const out = [];
      const usedGivers = new Set();
      for (const room of rooms) {
        if (out.length >= 3) break;
        const row = byRoomName(assignments, room.name);
        const crnaId = row?.ahpId || room.providerId;
        if (!crnaId || room.onBreak || room.relieverName) continue;
        if (getChannelState(room.breaks, "lunch") === "done") continue;
        const giver = (breakGivers || []).find(
          (g) => g && g.id !== crnaId && !usedGivers.has(g.id)
        );
        if (!giver) break;
        usedGivers.add(giver.id);
        out.push({
          kind: "break",
          roomId: room.id,
          roomName: room.name,
          channel: "lunch",
          giverId: giver.id,
          giverName: giver.name,
          label: `${giver.name} gives lunch in ${room.name} (${row?.ahpName || room.providerName || "CRNA"})`,
        });
      }
      if (!out.length) out.push({ kind: "answer", text: "No rooms need a lunch rotation right now (all done, on break, or no coverage available)." });
      return out;
    }

    case "ratio_query": {
      const counts = new Map();
      for (const a of assignments) {
        if (!a.attendingId) continue;
        const cur = counts.get(a.attendingId) || { name: a.attendingName || "?", n: 0 };
        cur.n += 1;
        counts.set(a.attendingId, cur);
      }
      if (!counts.size) return [{ kind: "answer", text: "No supervising MDs on today's board yet." }];
      const lines = [...counts.values()]
        .sort((a, b) => b.n - a.n)
        .map((c) => `${c.name}: ${c.n} room${c.n === 1 ? "" : "s"}${c.n > supervisorLimit ? ` ⚠ over the ${supervisorLimit}-room limit` : ""}`);
      return [{ kind: "answer", text: `Supervision ratios — ${lines.join(" · ")}` }];
    }

    case "board_query": {
      const staffed = assignments.filter((a) => a.ahpId || a.attendingId).length;
      const benchN = bench.length;
      return [{
        kind: "answer",
        text: `${staffed} staffed room${staffed === 1 ? "" : "s"}, ${benchN} free CRNA${benchN === 1 ? "" : "s"} on the bench.`,
      }];
    }

    case "admin_report":
      return [{
        kind: "report",
        text: intent.text,
        label: `File this for the dev loop: “${intent.text}”`,
      }];

    default:
      return [{ kind: "answer", text: "I didn't catch an action I know. Try: “assign a CRNA to OR 5”, “we need help in OR 2 and 3”, “rotate breaks”, or “what's our supervision ratio?”" }];
  }
}
